import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { Telegram2Icon, Typography } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { unsafeUrlDialogEvent } from '@root/entities/deeplinking/unsafe-url-dialog'
import {
  ProfileIcon,
  Separator,
  type DialogControlProps,
} from '@root/shared/ui-kit'

import { reatomReportTelegramGroupForm } from '../../atoms/reatom-report-telegram-group'
import { ReportForm } from '../common/report-form'

import { ReportDialog } from './report-dialog'

import type { ReportFormValues } from '../common/report-form'
import type { Socials } from '@root/entities/socials'

type ReportTelegramGroupDialogProps = {
  groupInfo: Socials.TgInfo
} & DialogControlProps

export const ReportTelegramGroupDialog: React.FC<
  ReportTelegramGroupDialogProps
> = ({ open, onOpenChange, groupInfo }) => {
  const { t } = useTranslation()
  const openUnsafeUrlDialog = useAction(unsafeUrlDialogEvent)

  const model = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomReportTelegramGroupForm({ groupInfo }),
    [groupInfo],
  )
  const [requestState] = useAtom(model)
  const sendReport = useAction(model.execute)

  const handleSubmit = (values: ReportFormValues) => {
    sendReport(values)
  }

  const handleLink = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    e.preventDefault()

    openUnsafeUrlDialog([groupInfo.portalLink, 'telegram'])
  }

  useEffect(() => {
    if (AsyncStateN.isSuccess(requestState)) {
      onOpenChange?.(false)
    }
  }, [onOpenChange, open, requestState])

  return (
    <ReportDialog open={open} onOpenChange={onOpenChange}>
      <Header>
        <Description>
          {t('report.report-telegram-group-description')}
        </Description>
        <GroupContainer>
          <GroupNameContainer onClick={handleLink}>
            <Telegram2Icon size={16} />
            <Typography ellipsis>{groupInfo.name}</Typography>
          </GroupNameContainer>
          <GroupSeparator />
          <MembersContainer>
            <ProfileIcon size={14} />
            <span>
              <Numeric
                value={groupInfo.members}
                preset="currency-amount-short"
              />
            </span>
          </MembersContainer>
        </GroupContainer>
      </Header>
      <StyledSeparator variant="gradient" />
      <ReportForm state={requestState} onSubmit={handleSubmit} />
    </ReportDialog>
  )
}

const Header = styled.div`
  display: grid;
  justify-content: center;
  justify-items: center;
  margin-bottom: 20px;
  margin-top: 8px;
`

const GroupContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 12px;
  align-items: center;

  border-radius: 8px;
  background: var(--appearanceA-2);

  padding-block: 11px;
  padding-inline: 14px;
`

const GroupNameContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 4px;
  align-items: center;

  color: var(--primary-10);
  cursor: pointer;

  font-size: 14px;
  font-weight: 500;
  line-height: 20px;

  &:hover {
    color: var(--primary-11);
  }
`

const GroupSeparator = styled.div`
  height: 24px;
  width: 1px;
  background: var(--backgroundA-3);
`

const MembersContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 2px;

  color: var(--text-11);
  font-size: 11px;
  font-weight: 400;
  line-height: 12px;
`

const Description = styled.div`
  color: var(--text-10);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  margin-bottom: 12px;
  text-align: center;
`

const StyledSeparator = styled(Separator)`
  margin-bottom: 16px;
`
